import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { ModalsService } from 'app/shared/ui/modals/modals.service';
import { LocalStorage } from 'app/shared/utils';
import { finalize, Observable, retry, tap } from 'rxjs';
import { AuthApi } from './auth-api.service';
import { invalidToken, login, logout, tokenNotPresent, updateUser, validTokenPresent } from './auth.actions';
import { getAuthLoggedIn } from './auth.reducer';
import { AuthLoginResponse } from './response.model';


@Injectable({
    providedIn: 'root',
})
export class AuthService {

    @LocalStorage('autopropToken') autopropToken: string | null;

    @LocalStorage('loggedInWithCrea') loggedInWithCrea: boolean | null;

    constructor(private authApi: AuthApi, private store: Store, private modalsService: ModalsService) {
        this.feedLoggedInState();
    }

    onLogin(data: AuthLoginResponse['payload']) {
        this.autopropToken = data.token;
        this.authApi.setToken(data.token);
        this.store.dispatch(login({ token: data.token }));
        this.store.dispatch(updateUser({ user: data.account, resources: data.allowedResources, disclaimers: data.disclaimers }));
    }

    authenticate(username: string, password: string): Observable<AuthLoginResponse> {
        return this.authApi.login(username, password);
    }

    logout() {
        return this.authApi.logout().pipe(
            finalize(() => {
                this.store.dispatch(logout());
                this.autopropToken = null;
            }),
        );
    }

    isAuthenticated() {
        return this.store.select(getAuthLoggedIn);
    }

    invalidateToken() {
        this.store.dispatch(invalidToken());
    }

    validateToken(token: string) {
        this.store.dispatch(validTokenPresent({ token }));
        this.authApi.setToken(token);
    }

    overrideToken(newToken: string) {
        this.autopropToken = newToken;
    }

    private feedLoggedInState() {
        const token = this.autopropToken;
        if (token) {
            this.authApi.tokenValidate(token).pipe(retry({ delay: 1000, count: 3 })).subscribe({
                next: res => {
                    if (res.status) {
                        const payload = res.payload;
                        this.validateToken(token);
                        this.store.dispatch(updateUser({
                            user: payload.account,
                            resources: payload.allowedResources,
                            disclaimers: payload.disclaimers,
                        }));
                    } else {
                        this.store.dispatch(tokenNotPresent());
                    }
                },
                error: () => {
                    this.modalsService.alert('Unable to load your session, please login or contact support.');
                    this.invalidateToken();
                    this.autopropToken = null;
                }
            });
        } else {
            this.store.dispatch(tokenNotPresent());
        }
    }

    agree(key: string, version: number, ignore: boolean) {
        return this.authApi.agree(key, version, ignore).pipe(tap(agreements => {
            this.store.dispatch(updateUser({ user: { agreements } }));
        }));
    }

    refreshUser() {
        return this.feedLoggedInState();
    }
}
